import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  CardHeader,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useDispatch } from "react-redux";

import EditCancel from "../../../components/EditCancel";
import useIppool from "../../../hooks/useIppool";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import IppoolFields from "./components/IppoolFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const validationSchema = Yup.object().shape({
  poolName: Yup.string().required("Required"),
  fromIp: Yup.string()
    .required("Required")
    .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
      message: "Invalid IP address",
      excludeEmptyString: true,
    }),
  toIp: Yup.string()
    .required("Required")
    .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
      message: "Invalid IP address",
      excludeEmptyString: true,
    }),
  comments: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
});

function BasicForm(props) {
  const dispatch = useDispatch();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
  } = methods;

  const { getIppool, ippool, updateIppool, isSubmitting } = useIppool();

  React.useEffect(() => {
    if (Object.keys(ippool).length !== 0) {
      const initialValues = {
        ipPoolId: ippool.ipPoolId,
        poolName: ippool.poolName,
        status: ippool.status,
        fromIp: ippool.fromIp,
        toIp: ippool.toIp,
        comments: ippool.comments,
      };
      reset(initialValues);
    }
  }, [ippool]);

  const { state } = useLocation();

  const { t } = props;
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      await updateIppool(data);
      reset();
      dispatch(showSuccessSnackbar("Ippool updated successfully!"));
      navigate("/radius/ippool/list");
    } catch (err) {
      dispatch(showErrorSnackbar(err.message));
    }
  };
  const cancelBtn = () => {
    navigate("/radius/ippool/list");
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Radius</Typography>
        <Typography>Ippool</Typography>
        <Typography>Edit</Typography>
      </Breadcrumbs>
    );
  };

  const fetchData = async () => {
    await getIppool(state.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("edit_ippool")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <IppoolFields fields={validationSchema.fields} />
                <br></br>
                <EditCancel cancelBtn={cancelBtn} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

function EditIppool() {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <BasicForm t={t} />
    </React.Fragment>
  );
}

export default EditIppool;
